/**
 * RTK Query endpoints for the roles service (@cluster/roles).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/roles`.
 */
import { apiSlice } from './apiSlice';
import type {
  Role,
  PolicySpec,
  RoleAssignment,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

/** Shape-agnostic flatten: accepts `T[]` or `{ data: T[] }`, returns `T[]`. */
function asList<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object' && 'data' in response) {
    const inner = (response as { data: unknown }).data;
    if (Array.isArray(inner)) return inner as T[];
  }
  return [];
}

export const rolesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRoles: builder.query<Role[], void>({
      query: () => '/roles',
      transformResponse: data<Role[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Role' as const, id: _id })),
              { type: 'Role', id: 'LIST' },
            ]
          : [{ type: 'Role', id: 'LIST' }],
    }),

    getRole: builder.query<Role, string>({
      query: (id) => `/roles/${id}`,
      transformResponse: data<Role>,
      providesTags: (_result, _error, id) => [{ type: 'Role', id }],
    }),

    createRole: builder.mutation<Role, Partial<Role> & { policySpecs?: PolicySpec[] }>({
      query: (body) => ({ url: '/roles', method: 'POST', body }),
      transformResponse: data<Role>,
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    updateRole: builder.mutation<Role, { id: string; body: Partial<Role> }>({
      query: ({ id, body }) => ({ url: `/roles/${id}`, method: 'PATCH', body }),
      transformResponse: data<Role>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Role', id }, { type: 'Role', id: 'LIST' }]
          : [{ type: 'Role', id: 'LIST' }],
    }),

    deleteRole: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/roles/${id}`, method: 'DELETE' }),
      invalidatesTags: [
        { type: 'Role', id: 'LIST' },
        { type: 'RoleAssignment', id: 'LIST' },
      ],
    }),

    /** POST /roles/:id/assign { userId } — materializes ephemeral policies. */
    assignRole: builder.mutation<RoleAssignment, { id: string; userId: string }>({
      query: ({ id, ...body }) => ({
        url: `/roles/${id}/assign`,
        method: 'POST',
        body,
      }),
      transformResponse: data<RoleAssignment>,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Role', id },
        { type: 'RoleAssignment', id },
        { type: 'RoleAssignment', id: 'LIST' },
      ],
    }),

    unassignRole: builder.mutation<{ success: boolean }, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/roles/${id}/unassign`,
        method: 'POST',
        body: { userId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Role', id },
        { type: 'RoleAssignment', id },
        { type: 'RoleAssignment', id: 'LIST' },
      ],
    }),

    refreshRole: builder.mutation<{ success: boolean }, { id: string; userId?: string }>({
      query: ({ id, ...body }) => ({
        url: `/roles/${id}/refresh`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Role', id },
        { type: 'RoleAssignment', id },
        { type: 'RoleAssignment', id: 'LIST' },
      ],
    }),

    getRoleAssignments: builder.query<RoleAssignment[], string>({
      query: (id) => `/roles/${id}/assignments`,
      transformResponse: asList<RoleAssignment>,
      providesTags: (_result, _error, id) => [
        { type: 'RoleAssignment', id },
        { type: 'RoleAssignment', id: 'LIST' },
      ],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignRoleMutation,
  useUnassignRoleMutation,
  useRefreshRoleMutation,
  useGetRoleAssignmentsQuery,
} = rolesApi;